import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaCheck, FaTimes, FaUserPlus } from 'react-icons/fa';
import { useToast } from '../../../components/ui/ToastProvider';
import { YButton } from '../../../ui/YButton';
import { isProRequiredError } from '../../pro/entitlements';
import { ProPanel } from '../../pro/ProPanel';
import {
  acceptClubMemberInvite,
  acceptedInvitePath,
  declineClubMemberInvite,
  fetchMyMemberInvites,
  memberInviteErrorMessage,
} from '../services/memberInviteService';
import './ClubMemberInvitesInbox.css';

export function ClubMemberInvitesInbox() {
  const location = useLocation();
  const navigate = useNavigate();
  const { show: showToast } = useToast();
  const sectionRef = useRef(null);
  const [invites, setInvites] = useState([]);
  const [error, setError] = useState('');
  const [actingId, setActingId] = useState(null);
  const [proRequired, setProRequired] = useState(false);

  const loadInvites = useCallback(async () => {
    try {
      const rows = await fetchMyMemberInvites({ status: 'pending' });
      setInvites(rows);
      setError('');
    } catch (err) {
      setInvites([]);
      setError(memberInviteErrorMessage(err, 'Could not load club invites.'));
    }
  }, []);

  useEffect(() => {
    loadInvites();
  }, [loadInvites]);

  useEffect(() => {
    if (!location.state?.focusClubInvites || invites.length === 0) return;
    const timer = window.setTimeout(() => {
      sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [location.state?.focusClubInvites, invites.length]);

  const handleAccept = async (invite) => {
    setActingId(invite.id);
    setError('');
    try {
      const data = await acceptClubMemberInvite(invite.id);
      setInvites((prev) => prev.filter((row) => row.id !== invite.id));
      try {
        showToast?.({
          title: 'Invite accepted',
          body: `You joined ${invite.club_name || 'the club'}.`,
          variant: 'success',
        });
      } catch (_) {
        /* toast optional in tests */
      }
      navigate(acceptedInvitePath(data?.invite || invite));
    } catch (err) {
      if (isProRequiredError(err, 'clubs')) {
        setProRequired(true);
        return;
      }
      setError(memberInviteErrorMessage(err, 'Could not accept that invite.'));
    } finally {
      setActingId(null);
    }
  };

  const handleDecline = async (invite) => {
    setActingId(invite.id);
    setError('');
    try {
      await declineClubMemberInvite(invite.id);
      setInvites((prev) => prev.filter((row) => row.id !== invite.id));
      try {
        showToast?.({
          title: 'Invite declined',
          body: invite.club_name ? `You declined ${invite.club_name}.` : 'Invite declined.',
          variant: 'info',
        });
      } catch (_) {
        /* toast optional */
      }
    } catch (err) {
      setError(memberInviteErrorMessage(err, 'Could not decline that invite.'));
    } finally {
      setActingId(null);
    }
  };

  if (proRequired) {
    return (
      <div data-testid="club-member-invite-pro-gate">
        <ProPanel onDismiss={() => setProRequired(false)} />
      </div>
    );
  }

  if (!error && invites.length === 0) return null;

  return (
    <div
      ref={sectionRef}
      className="club-member-invites-inbox"
      data-testid="club-member-invites-inbox"
    >
      <div className="club-member-invites-inbox__head">
        <FaUserPlus aria-hidden />
        <h2 className="club-member-invites-inbox__title">Club invites</h2>
      </div>
      <p className="club-member-invites-inbox__hint">
        A club president invited you. Accept to join the roster.
      </p>
      {error ? (
        <p className="club-member-invites-inbox__error" role="alert">
          {error}
        </p>
      ) : null}
      {invites.length > 0 ? (
        <ul className="club-member-invites-inbox__list">
          {invites.map((invite) => {
            const busy = actingId === invite.id;
            return (
              <li
                key={invite.id}
                className="club-member-invites-inbox__item"
                data-testid={`club-member-invite-${invite.id}`}
              >
                <div className="club-member-invites-inbox__meta">
                  <strong>{invite.club_name || 'Club invite'}</strong>
                  {invite.invited_by_name ? (
                    <span className="club-member-invites-inbox__from">
                      from {invite.invited_by_name}
                    </span>
                  ) : null}
                </div>
                <div className="club-member-invites-inbox__actions">
                  <YButton
                    type="button"
                    variant="primary"
                    size="sm"
                    disabled={busy}
                    onClick={() => handleAccept(invite)}
                    data-testid={`club-member-invite-accept-${invite.id}`}
                  >
                    {busy ? <Spinner animation="border" size="sm" role="status" /> : (
                      <>
                        <FaCheck aria-hidden className="me-1" />
                        Accept
                      </>
                    )}
                  </YButton>
                  <YButton
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={busy}
                    onClick={() => handleDecline(invite)}
                    data-testid={`club-member-invite-decline-${invite.id}`}
                  >
                    <FaTimes aria-hidden className="me-1" />
                    Decline
                  </YButton>
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

export default ClubMemberInvitesInbox;
